import { PageHeaderLink } from "@/components/PageHeaderLink";
import { Shell } from "@/components/Shell";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import {
	Empty,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
	EmptyTitle,
} from "@/components/ui/empty";
import {
	Field,
	FieldDescription,
	FieldError,
	FieldGroup,
	FieldLabel,
	FieldSet,
} from "@/components/ui/field";
import { useForm } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
	InputGroup,
	InputGroupAddon,
	InputGroupInput,
} from "@/components/ui/input-group";
import {
	Item,
	ItemActions,
	ItemContent,
	ItemMedia,
	ItemTitle,
} from "@/components/ui/item";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { cn, formatRelativeTime } from "@/lib/utils";
import {
	useMutation,
	useQueryClient,
	useSuspenseQuery,
} from "@tanstack/react-query";
import { AlertCircleIcon, ChevronRightIcon, RotateCcwIcon } from "lucide-react";
import { Fragment, Suspense, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { z } from "zod";

interface Service {
	id: string;
	name: string;
	url: string;
	endpoint: string;
	lastCheckedAt: string | null;
	lastStatus: string | null;
	incidentMinDowntimeMinutes: number | null;
}

interface Incident {
	id: string;
	startedAt: string;
	endedAt: string | null;
	durationMinutes: number | null;
	message: string | null;
	status: string | null;
}

async function fetchService(
	appId: string,
	serviceId: string,
): Promise<Service | null> {
	const res = await fetch(`/api/applications/${appId}/services/${serviceId}`, {
		credentials: "include",
	});
	if (res.status === 404) return null;
	if (!res.ok) throw new Error("Failed to load");
	return res.json() as Promise<Service>;
}

async function fetchIncidents(
	appId: string,
	serviceId: string,
): Promise<Incident[]> {
	const res = await fetch(
		`/api/applications/${appId}/services/${serviceId}/incidents`,
		{ credentials: "include" },
	);
	if (!res.ok) return [];
	const data = (await res.json()) as { incidents: Incident[] };
	return data.incidents ?? [];
}

const schema = z
	.object({
		name: z
			.string("Name is required")
			.min(1, "Name is required")
			.max(255, "Name is too long"),
		url: z.url("Base URL is invalid"),
		endpoint: z
			.string("Endpoint path is required")
			.min(1, "Endpoint path is required"),
		automaticallyCreateIncidents: z.boolean(),
		incidentMinDowntimeMinutes: z.number().int().min(1).nullable(),
	})
	.superRefine((value, ctx) => {
		if (
			value.automaticallyCreateIncidents &&
			value.incidentMinDowntimeMinutes == null
		) {
			ctx.addIssue({
				code: "custom",
				path: ["incidentMinDowntimeMinutes"],
				message:
					"Min downtime is required when incidents are created automatically",
			});
		}
	});

const LoadingState = () => {
	return (
		<Fragment>
			<Skeleton className="h-5 w-28" />
			<Skeleton className="h-80 w-full" />
			<Skeleton className="h-14 w-full" />
			<Skeleton className="h-36 w-full" />
		</Fragment>
	);
};

const EmptyState = ({ appId }: { appId?: string }) => {
	return (
		<Fragment>
			<PageHeaderLink to={appId ? `/${appId}` : "/"} label="Application" />
			<Empty>
				<EmptyHeader>
					<EmptyMedia variant="icon">
						<AlertCircleIcon />
					</EmptyMedia>
					<EmptyTitle>Service not found</EmptyTitle>
					<EmptyDescription>
						This service may have been deleted or you may not have access.
					</EmptyDescription>
				</EmptyHeader>
			</Empty>
		</Fragment>
	);
};

const Content = ({
	appId,
	serviceId,
}: {
	appId: string;
	serviceId: string;
}) => {
	const queryClient = useQueryClient();
	const serviceQuery = useSuspenseQuery({
		queryKey: ["service", appId, serviceId],
		queryFn: () => fetchService(appId, serviceId),
	});
	const incidentsQuery = useSuspenseQuery({
		queryKey: ["incidents", appId, serviceId],
		queryFn: () => fetchIncidents(appId, serviceId),
	});

	const queryError = serviceQuery.error ?? incidentsQuery.error;
	const service = serviceQuery.data;
	const incidents = incidentsQuery.data ?? [];

	const updateService = useMutation({
		mutationFn: (body: {
			name: string;
			url: string;
			endpoint: string;
			incidentMinDowntimeMinutes?: number | null;
		}) =>
			fetch(`/api/applications/${appId}/services/${serviceId}`, {
				method: "PATCH",
				headers: { "Content-Type": "application/json" },
				credentials: "include",
				body: JSON.stringify(body),
			}).then(async (res) => {
				const data = (await res.json().catch(() => null)) as {
					error?: string;
				} | null;
				if (!res.ok) {
					throw new Error(data?.error ?? "Failed to update service");
				}
				return data;
			}),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["service", appId, serviceId],
			});
		},
	});

	const updateIncident = useMutation({
		mutationFn: (params: {
			incidentId: string;
			message: string | null;
			status: string | null;
		}) =>
			fetch(
				`/api/applications/${appId}/services/${serviceId}/incidents/${params.incidentId}`,
				{
					method: "PATCH",
					headers: { "Content-Type": "application/json" },
					credentials: "include",
					body: JSON.stringify({
						message: params.message,
						status: params.status,
					}),
				},
			),
		onSuccess: (res) => {
			if (res.ok)
				queryClient.invalidateQueries({
					queryKey: ["incidents", appId, serviceId],
				});
		},
	});

	const triggerCheck = useMutation({
		mutationFn: () =>
			fetch(`/api/applications/${appId}/services/${serviceId}/check`, {
				method: "POST",
				credentials: "include",
			}),
		onSuccess: (res) => {
			if (res.ok)
				queryClient.invalidateQueries({ queryKey: ["services", appId] });
		},
	});

	const form = useForm<z.infer<typeof schema>>({
		validators: {
			onSubmit: schema,
		},
		defaultValues: {
			name: "",
			url: "",
			endpoint: "/",
			automaticallyCreateIncidents: false,
			incidentMinDowntimeMinutes: null as number | null,
		},
		onSubmit: async ({ value }) => {
			await updateService.mutateAsync({
				name: value.name.trim(),
				url: value.url.trim(),
				endpoint: value.endpoint || "/",
				incidentMinDowntimeMinutes: value.automaticallyCreateIncidents
					? value.incidentMinDowntimeMinutes
					: null,
			});
		},
	});

	useEffect(() => {
		if (!service) return;
		form.reset({
			name: service.name,
			url: service.url,
			endpoint: service.endpoint,
			automaticallyCreateIncidents: service.incidentMinDowntimeMinutes != null,
			incidentMinDowntimeMinutes: service.incidentMinDowntimeMinutes ?? null,
		});
	}, [service, form]);

	if (queryError) {
		return <div className="text-destructive">{queryError.message}</div>;
	}
	if (!service) return <EmptyState appId={appId} />;

	return (
		<Fragment>
			<PageHeaderLink to={`/${appId}`} label="Application" />
			<Card>
				<CardHeader>
					<CardTitle>Service</CardTitle>
					<CardDescription className="flex flex-wrap items-center gap-2">
						<StatusBadge status={service.lastStatus} />
						{service.lastCheckedAt ? (
							<span>Updated {formatRelativeTime(service.lastCheckedAt)}</span>
						) : (
							<span>Never checked</span>
						)}
					</CardDescription>
				</CardHeader>
				<CardContent className="space-y-4">
					<form
						id="service-config"
						onSubmit={(e) => {
							e.preventDefault();
							e.stopPropagation();
							form.handleSubmit();
						}}
					>
						{updateService.error && (
							<p
								className="text-sm text-destructive bg-destructive/10 p-2 rounded"
								role="alert"
							>
								{updateService.error.message}
							</p>
						)}
						<FieldGroup>
							<FieldSet>
								<form.Field name="name">
									{(field) => (
										<Field>
											<FieldLabel htmlFor="name">Name</FieldLabel>
											<Input
												id="name"
												value={field.state.value}
												onChange={(e) => field.handleChange(e.target.value)}
												onBlur={field.handleBlur}
												disabled={updateService.isPending}
											/>
											<FieldError errors={field.state.meta.errors} />
										</Field>
									)}
								</form.Field>
								<form.Field name="url">
									{(field) => (
										<Field>
											<FieldLabel htmlFor="url">Base URL</FieldLabel>
											<Input
												id="url"
												type="url"
												value={field.state.value}
												onChange={(e) => field.handleChange(e.target.value)}
												onBlur={field.handleBlur}
												disabled={updateService.isPending}
											/>
											<FieldError errors={field.state.meta.errors} />
										</Field>
									)}
								</form.Field>
								<form.Field name="endpoint">
									{(field) => (
										<Field>
											<FieldLabel htmlFor="endpoint">Endpoint path</FieldLabel>
											<Input
												id="endpoint"
												value={field.state.value}
												onChange={(e) => field.handleChange(e.target.value)}
												onBlur={field.handleBlur}
												disabled={updateService.isPending}
											/>
											<FieldError errors={field.state.meta.errors} />
										</Field>
									)}
								</form.Field>
								<form.Field name="automaticallyCreateIncidents">
									{(field) => (
										<Field orientation="horizontal">
											<div className="space-y-1">
												<FieldLabel htmlFor="autoIncidents">
													Automatically create incidents
												</FieldLabel>
												<Switch
													id="autoIncidents"
													checked={field.state.value}
													onCheckedChange={(checked) => {
														const isChecked = checked === true;
														field.handleChange(isChecked);
														if (!isChecked) {
															form.setFieldValue(
																"incidentMinDowntimeMinutes",
																null,
															);
														}
													}}
													disabled={updateService.isPending}
												/>
												<FieldDescription>
													When enabled, an incident is created automatically
													after a minimum downtime threshold.
												</FieldDescription>
											</div>
										</Field>
									)}
								</form.Field>
								<form.Subscribe
									selector={(state) =>
										state.values.automaticallyCreateIncidents
									}
								>
									{(automaticallyCreateIncidents) =>
										automaticallyCreateIncidents ? (
											<form.Field name="incidentMinDowntimeMinutes">
												{(field) => (
													<Field>
														<FieldLabel htmlFor="incidentMin">
															Downtime threshold
														</FieldLabel>
														<InputGroup>
															<InputGroupInput
																id="incidentMin"
																type="number"
																min={1}
																placeholder="60"
																value={
																	field.state.value == null
																		? ""
																		: String(field.state.value)
																}
																onChange={(e) => {
																	const value = e.target.value;
																	field.handleChange(
																		value === "" ? null : Number(value),
																	);
																}}
																onBlur={field.handleBlur}
																disabled={updateService.isPending}
															/>
															<InputGroupAddon align="inline-end">
																minutes
															</InputGroupAddon>
														</InputGroup>
														<FieldDescription>
															When downtime lasts at least this many minutes, an
															incident is automatically created.
														</FieldDescription>
														<FieldError errors={field.state.meta.errors} />
													</Field>
												)}
											</form.Field>
										) : null
									}
								</form.Subscribe>
							</FieldSet>
						</FieldGroup>
					</form>
				</CardContent>
				<CardFooter className="grid grid-cols-2 gap-4">
					<Button
						type="button"
						variant="outline"
						className="w-full"
						onClick={() => triggerCheck.mutate()}
						disabled={triggerCheck.isPending}
						aria-label="Check now"
						title="Check now"
					>
						<RotateCcwIcon className="size-4" />
						Check now
					</Button>
					<form.Subscribe
						selector={(state) => [
							state.isSubmitting,
							state.isValid,
							state.isDirty,
						]}
					>
						{([isSubmitting, isValid, isDirty]) => (
							<Button
								type="submit"
								form="service-config"
								className="w-full"
								disabled={!isValid || !isDirty}
								loading={isSubmitting || updateService.isPending}
							>
								Save
							</Button>
						)}
					</form.Subscribe>
				</CardFooter>
			</Card>

			<Item variant="outline" size="sm" asChild>
				<Link
					to={{
						pathname: "/incidents",
						search: `?appId=${appId}&serviceId=${serviceId}`,
					}}
				>
					<ItemMedia>
						<AlertCircleIcon className="size-5" />
					</ItemMedia>
					<ItemContent>
						<ItemTitle className="flex items-center gap-2">
							Incidents recorded
							<span className="text-xs rounded-full bg-muted size-6 font-mono flex items-center justify-center tabular-nums">
								{incidents.length}
							</span>
						</ItemTitle>
					</ItemContent>
					<ItemActions>
						<ChevronRightIcon className="size-4" />
					</ItemActions>
				</Link>
			</Item>

			{incidents.length > 0 && (
				<Card>
					<CardHeader>
						<CardTitle>Incidents</CardTitle>
						<CardDescription>
							Add a message and status to incidents (shown on the status page
							tooltip).
						</CardDescription>
					</CardHeader>
					<CardContent className="space-y-4">
						{incidents.map((inc) => (
							<IncidentEditor
								key={inc.id}
								incident={inc}
								onSave={(message, status) =>
									updateIncident.mutateAsync({
										incidentId: inc.id,
										message,
										status,
									})
								}
								isPending={updateIncident.isPending}
							/>
						))}
					</CardContent>
				</Card>
			)}
		</Fragment>
	);
};

export function ServiceConfig() {
	const { appId, serviceId } = useParams<{
		appId: string;
		serviceId: string;
	}>();

	return (
		<Shell className="flex flex-col gap-4">
			<Suspense fallback={<LoadingState />}>
				{appId && serviceId ? (
					<Content appId={appId} serviceId={serviceId} />
				) : (
					<EmptyState appId={appId} />
				)}
			</Suspense>
		</Shell>
	);
}

const INCIDENT_STATUS_OPTIONS = [
	"",
	"investigating",
	"identified",
	"monitoring",
	"resolved",
] as const;

function IncidentEditor({
	incident,
	onSave,
	isPending,
}: {
	incident: Incident;
	onSave: (message: string | null, status: string | null) => Promise<unknown>;
	isPending: boolean;
}) {
	const [message, setMessage] = useState(incident.message ?? "");
	const [status, setStatus] = useState(incident.status ?? "");
	const [saving, setSaving] = useState(false);

	const started = new Date(incident.startedAt).toLocaleString();
	const ended = incident.endedAt
		? new Date(incident.endedAt).toLocaleString()
		: "\u2014";
	const duration =
		incident.durationMinutes != null ? `${incident.durationMinutes} min` : "\u2014";

	const handleSave = async () => {
		setSaving(true);
		try {
			await onSave(message.trim() || null, status.trim() || null);
		} finally {
			setSaving(false);
		}
	};

	return (
		<div className="rounded-md border border-border p-4 space-y-3">
			<p className="text-sm text-muted-foreground tabular-nums">
				Started: {started} · Ended: {ended} · Duration: {duration}
			</p>
			<div className="space-y-2">
				<Label>Status</Label>
				<Select
					value={status || " "}
					onValueChange={(v) => setStatus(v === " " ? "" : v)}
					disabled={isPending || saving}
				>
					<SelectTrigger
						className={cn("w-full max-w-xs capitalize", {
							"text-emerald-500": status === "resolved",
							"text-destructive": status === "investigating",
							"text-muted-foreground": status === "monitoring",
							"text-amber-500": status === "identified",
						})}
					>
						<SelectValue placeholder="Select status" />
					</SelectTrigger>
					<SelectContent>
						{INCIDENT_STATUS_OPTIONS.map((opt) => (
							<SelectItem
								key={opt || "empty"}
								value={opt || " "}
								className={cn("capitalize", {
									"text-emerald-500": opt === "resolved",
									"text-destructive": opt === "investigating",
									"text-muted-foreground": opt === "monitoring",
									"text-amber-500": opt === "identified",
								})}
							>
								{opt || "(none)"}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			</div>
			<div className="space-y-2">
				<Label>Message</Label>
				<Textarea
					placeholder="Optional update for users"
					value={message}
					onChange={(e) => setMessage(e.target.value)}
					disabled={isPending || saving}
					rows={3}
					className="resize-none"
				/>
			</div>
			<Button
				type="button"
				size="sm"
				onClick={handleSave}
				disabled={isPending || saving}
			>
				{saving ? "Saving…" : "Save"}
			</Button>
		</div>
	);
}
